import { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Input, Spin, Select, DatePicker, TimePicker, Table, Popover } from "antd";
import DoctorSelect from "../../../../../Components/Dropdowns/DoctorSelect";
import AgentSelect from "../../../../../Components/Dropdowns/AgentSelect";
import PaymentTypeSelect from "../../../../../Components/Dropdowns/PaymentTypeSelect";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { usePrintReport } from "../../../../../Components/PrintReport";
import Sidebar from "../../GlobalFiles/Sidebar";
import {
  Search,
  Trash2,
  Calendar,
  User,
  Phone,
  Heart,
  DollarSign,
  Clock,
  ChevronLeft,
  ChevronRight,
  FileText,
  Building,
  Filter,
  Edit,
  TestTube2Icon,
  PlusCircleIcon,
} from "lucide-react";
import moment from "moment";
import { API_BASE_URL } from "../../../../../api";
import TestsTreeView from "../../../../../Components/TestsTreeView";


// TODO: This File has Some Duplicate Key Warnings to be Fixed 

const TestNamePill = ({ name }) => {
  const [open, setOpen] = useState(false);
  const displayName = name || "N/A";

  return (
    <Popover
      content={
        <div className="max-w-xs break-words text-sm text-gray-800">
          {displayName}
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <button
        type="button"
        className="text-sm text-gray-700 bg-blue-400 text-black px-2 py-0.5 rounded-full truncate max-w-[160px]"
        onClick={(e) => e.stopPropagation()}
      >
        {displayName}
      </button>
    </Popover>
  );
};

const TestOrdersList = () => {
  const { data: { user } = {} } = useSelector((state) => state.auth || {});
  const { printReport } = usePrintReport();

  // Only admins can recalculate commissions
  const isAdmin = user?.userType === 'admin';

  const getCurrentUserName = () => {
    if (!user) return "System";
    return (
      user.managerName ||
      user.doctorName ||
      user.adminName ||
      user.name ||
      "Unknown User"
    );
  };

  const [testOrders, setTestOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({});
  const [isExternalDoctor, setIsExternalDoctor] = useState(false);

  const [isAddTestLoading, setIsAddTestLoading] = useState(false);
  const [isTestSelectionModalOpen, setIsTestSelectionModalOpen] = useState(false);
  const [isRemoveTestLoading, setIsRemoveTestLoading] = useState(false);

  const [dataHasChanged, setDataHasChanged] = useState(false);
  const [testAddStep, setTestAddStep] = useState(1);
  const [selectedTests, setSelectedTests] = useState([]);

  const [isPatientEditModalOpen, setIsPatientEditModalOpen] = useState(false);
  const [patientFormData, setPatientFormData] = useState({});
  const [isPatientUpdating, setIsPatientUpdating] = useState(false);

  const handlePatientEditClick = () => {
    // Check if current user is a manager
    const isManager = Boolean(user?.managerName || user?.role === "manager" || user?.userType === "manager");
    if (isManager) {
      toast.warn("Only Admins can edit patient information. Please ask an Admin to edit.");
      return;
    }

    if (!selectedOrder?.patient?._id && !selectedOrder?.patientID) {
      toast.error("Patient details not found for this test order.");
      return;
    }

    const currentPatient = selectedOrder?.patient || {};
    setPatientFormData({
      patientID: selectedOrder.patientID || currentPatient.patientID || "",
      patientName: currentPatient.patientName || selectedOrder.patientName || "",
      age: currentPatient.age || selectedOrder.age || "",
      gender: currentPatient.gender || selectedOrder.gender || "",
      mobile: currentPatient.mobile || selectedOrder.mobile || "",
      email: currentPatient.email || selectedOrder.email || "",
      address: currentPatient.address || selectedOrder.address || "",
    });
    setIsPatientEditModalOpen(true);
  };

  const handlePatientFormChange = (e) => {
    const { name, value } = e.target;
    setPatientFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePatientUpdateSubmit = async () => {
    const patientId = selectedOrder?.patient?._id;
    if (!patientId) {
      toast.error("Patient ID not found.");
      return;
    }

    try {
      setIsPatientUpdating(true);
      await axios.patch(`${API_BASE_URL}/patients/${patientId}`, patientFormData);
      toast.success("Patient details updated successfully!");
      setIsPatientEditModalOpen(false);

      // Update selectedOrder state locally
      setSelectedOrder((prev) => ({
        ...prev,
        patientName: patientFormData.patientName,
        age: patientFormData.age,
        gender: patientFormData.gender,
        mobile: patientFormData.mobile,
        email: patientFormData.email,
        address: patientFormData.address,
        patient: {
          ...(prev?.patient || {}),
          ...patientFormData,
        },
      }));

      fetchTestOrders();
    } catch (error) {
      console.error("Error updating patient details:", error);
      toast.error(error.response?.data?.message || error.response?.data?.error || "Failed to update patient details.");
    } finally {
      setIsPatientUpdating(false);
    }
  };

  const itemsPerPage = 10;

  const fetchTestOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/testorders?depth=2`);
      setTestOrders(response.data);
      // console.log("Fetched test orders:", response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching test orders:", error);
      toast.error("Error loading test orders: " + (error.message || "Unknown error"));
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchTestOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API_BASE_URL}/testorders/${orderId}/status`, {
        status: newStatus,
      });
      toast.success(`Order status updated to ${newStatus}`);
      fetchTestOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const deleteTestOrder = async (orderId) => {
    try {
      await axios.delete(`${API_BASE_URL}/testorders/${orderId}`);
      toast.success("Test order deleted successfully");
      setIsConfirmDeleteOpen(false);
      setOrderToDelete(null);
      setTestOrders((prev) => {
        const next = (prev || []).filter((order) => order._id !== orderId);
        const nextTotalPages = Math.max(1, Math.ceil(next.length / itemsPerPage));
        setCurrentPage((page) => Math.min(page, nextTotalPages));
        return next;
      });
      if (selectedOrder?._id === orderId) {
        setSelectedOrder(null);
        setIsModalVisible(false);
        setIsEditModalVisible(false);
      }
    } catch (error) {
      console.error("Error deleting test order:", error);
      toast.error("Failed to delete test order");
    }
  };

  const recalculateCommission = async (orderId) => {
    // Frontend guard — backend also enforces this
    if (!isAdmin) {
      toast.warn("Please ask an admin to do this.");
      return;
    }
    try {
      const res = await axios.post(
        `${API_BASE_URL}/testorders/${orderId}/recalculate-commission`,
        {},
        { headers: { "x-actor-type": "admin", "x-actor-id": user?._id || "", "x-actor-name": user?.adminName || "" } }
      );
      toast.success(res.data?.message || "Commission recalculated");
      fetchTestOrders();
    } catch (error) {
      console.error("Error recalculating commission:", error);
      toast.error(error.response?.data?.message || "Failed to recalculate commission");
    }
  };

  const handleEdit = async () => {
    const payDueAmount = Number(formData.payDueAmount || 0);
    const discountOnDue = Number(formData.discountOnDue || 0);
    let didSomething = false;
    let errorOccurred = false;

    let latestOrder = selectedOrder;
    try {
      const baseAmount = Number(formData.baseAmount !== undefined && formData.baseAmount !== "" ? formData.baseAmount : (latestOrder.baseAmount || 0));
      const prevDiscount = Number(latestOrder.discountAmount || 0);
      const newDiscount = prevDiscount + discountOnDue;
      const newTotalAmount = Math.max(baseAmount - newDiscount, 0);
      const prevPaid = Number(latestOrder.paidAmount || 0);
      const newPaid = prevPaid + payDueAmount;
      let newDue = Math.max(newTotalAmount - newPaid, 0);

      const formattedData = {
        ...formData,
        baseAmount,
        discountAmount: newDiscount,
        totalAmount: newTotalAmount,
        paidAmount: newPaid,
        dueAmount: newDue,
        payDueAmount: payDueAmount > 0 ? payDueAmount : undefined,
        date: formData.date ? moment(formData.date).format("YYYY-MM-DD") : undefined,
        time: formData.time ? moment(formData.time).format("HH:mm") : undefined,
        discountOnDue: '',
        agent: formData.agent || null,
        ...(formData.doctor ? { doctor: formData.doctor } : {}),
        ...(formData.externalDoctorName ? { externalDoctorName: formData?.externalDoctorName } : {})
      };

      await axios.put(`${API_BASE_URL}/testorders/${selectedOrder._id}`, formattedData);
      toast.success(payDueAmount > 0 ? 'Updated & due payment successful!' : 'Updated successfully!');
      didSomething = true;

      setFormData((prev) => ({
        ...prev,
        payDueAmount: '',
        discountOnDue: '',
        discountAmount: newDiscount,
        totalAmount: newTotalAmount,
        paidAmount: newPaid,
        dueAmount: newDue,
      }));
    } catch (error) {
      errorOccurred = true;
      console.error("Error updating test order:", error);
      toast.error("Error occurred: " + (error.response?.data?.message || 'Failed to Update!'));
    }

    if (didSomething && !errorOccurred) {
      fetchTestOrders();
      setIsEditModalVisible(false);
      setSelectedOrder(null);
    }
    setDataHasChanged(false);
  };

  const handlePrintReport = (order) => {
    printReport(order);
  };

  const filteredOrders = testOrders?.filter(
    (order) =>
      order.testOrderID?.toString().includes(searchQuery) ||
      order.patient?.patientID?.toString().includes(searchQuery) ||
      order.patient?.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.patient?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.patient?.mobile &&
        typeof order.patient?.mobile === "string" &&
        order.patient.mobile.includes(searchQuery)) ||
      order.doctor?.docName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.externalDoctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.agent?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )?.filter((order) => {
    if (!selectedDate) return true;
    const orderDate = order.createdAt ? new Date(order.createdAt).toLocaleDateString() : null;
    const selectedStr = selectedDate ? selectedDate.toDate().toLocaleDateString() : null;
    return orderDate === selectedStr;
  });

  const sortedOrders = filteredOrders?.sort((a, b) => {
    if (sortBy === "schedule") {
      return new Date(a.date) - new Date(b.date);
    } else {
      const dateA = new Date(a.createdAt || a._id);
      const dateB = new Date(b.createdAt || b._id);
      return dateB - dateA;
    }
  });

  const totalPages = Math.ceil((sortedOrders?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = sortedOrders?.slice(startIndex, endIndex);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setFormData({
      doctor: order.doctor?._id || "",
      externalDoctorName: order.externalDoctorName || "",
      agent: order.agent?._id || "",

      date: moment(order.date).format("YYYY-MM-DD"),
      time: moment(order.time, "HH:mm"),
      baseAmount: order.baseAmount || 0,

      paymentType: order.paymentType || "",
      discountAmount: order.discountAmount || 0,

      paidAmount: order.paidAmount || 0,
      totalAmount: order.totalAmount,
      dueAmount: order.dueAmount || 0,

      hospitalRevenue: order.hospitalRevenue,
      doctorRevenue: order.doctorRevenue,
      agentRevenue: order.agentRevenue,
    });
    setSelectedTests([]);
    setIsExternalDoctor(!!order.externalDoctorName);
    setIsEditModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedOrder(null);
    setFormData({});
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setDataHasChanged(true);
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date });
    setDataHasChanged(true);
  };

  const handleTimeChange = (time) => {
    // console.log("Selected time:", time);
    if (time) {
      time = time.format('HH:mm');
    }
    setFormData({ ...formData, time });
    setDataHasChanged(true);
  };

  const getStatusColor = (order) => {
    if ((order.dueAmount || 0) === 0) return "bg-emerald-100 text-emerald-700";
    return "bg-blue-100 text-blue-700";
  };

  const handleRemoveTest = async (testId) => {
    setIsRemoveTestLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/testorders/${selectedOrder._id}/remove-test/`, {
        testId
      });
      // console.log("Removing test from order:", selectedOrder);
      // console.log("Remove test response:", res.data, "Test Id:", testId);

      if (res.status === 200) {
        const updatedOrder = {
          ...selectedOrder,
          tests: res.data.tests,
          totalAmount: res.data.totalAmount,
          baseAmount: res.data.baseAmount,
          dueAmount: res.data.dueAmount,
        };

        setTestOrders((prevOrders) => prevOrders.map((order) =>
          order._id === updatedOrder._id ? updatedOrder : order
        )
        );
        setSelectedOrder(updatedOrder);
        setFormData((prev) => ({
          ...prev,
          totalAmount: res.data.totalAmount,
          baseAmount: res.data.baseAmount,
          dueAmount: res.data.dueAmount,
        }));

        // console.log("TestId we are removing:", testId);
        toast.success("Test removed from order successfully");
      } else {
        toast.error("Failed to remove test from order");
      }
    } catch (error) {
      console.error("Error removing test from order:", error);
      toast.error("Failed to remove test from order");
    } finally {
      setIsRemoveTestLoading(false);
    }
  }

  const handleAddTestsToOrder = async (tests) => {

    // console.log("handleAddTestsToOrder called with tests:", tests);
    setIsAddTestLoading(true);
    try {
      // console.log("Adding tests to order:", selectedOrder);
      const res = await axios.post(`${API_BASE_URL}/testorders/${selectedOrder._id}/add-tests/`, tests);
      // console.log("Add test response:", res.data.tests);

      if (res.status === 200) {
        const updatedOrder = {
          ...selectedOrder,
          tests: res.data.tests,
          totalAmount: res.data.totalAmount,
          baseAmount: res.data.baseAmount,
          dueAmount: res.data.dueAmount,
        };
        setTestOrders((prevOrders) => prevOrders.map((order) =>
          selectedOrder._id === updatedOrder._id ? updatedOrder : order
        )
        );

        setFormData((prev) => ({
          ...prev,
          totalAmount: res.data.totalAmount,
          baseAmount: res.data.baseAmount,
          dueAmount: res.data.dueAmount,
        }));

        setSelectedOrder(updatedOrder);
        setSelectedTests([]);
        setIsTestSelectionModalOpen(false);
        setTestAddStep(1);
        setIsEditModalVisible(true);
        toast.success("Test added to order successfully");

        return 1;
      } else {
        toast.error("Failed to add test to order");
      }
    } catch (error) {
      console.error("Error adding test to order:", error);
      toast.error("Failed to add test to order");
    } finally {
      setIsAddTestLoading(false);
    }

    return 0;
  }

  return (
    <>
      <div className="flex min-h-screen bg-gray-50 overflow-x-hidden">
        <Sidebar onCollapse={setSidebarCollapsed} />

        {<Modal
          title="Select Tests"
          open={isTestSelectionModalOpen}
          onCancel={() => {
            setIsTestSelectionModalOpen(false);
            setIsEditModalVisible(true);
          }}
          footer={
            <Button onClick={() => {
              // setIsTestSelectionModalOpen(false);
              // setIsEditModalVisible(true);
              if (testAddStep === 1) {
                setTestAddStep(2);
              } else {
                setTestAddStep(1);
              }
            }}>
              {testAddStep === 1 ? "Next" : "Back"}
            </Button>}
        >
          {isAddTestLoading ?
            <div className="flex flex-col items-center justify-center py-16">
              <Spin size="large" />
            </div>
            : <TestsTreeView
              onSelectTests={handleAddTestsToOrder}
              excludeTestIds={selectedOrder?.tests?.map(t => t._id) || []}
              step={testAddStep}
              selectedTests={selectedTests}
              setSelectedTests={setSelectedTests}
            />}
        </Modal>
        }

        <div className={`flex-1 min-w-0 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`} style={{ overflowX: 'hidden' }}>
          <div className="flex-1 min-w-0 p-6 ml-40">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">Test Orders</h1>
                      <p className="text-gray-600">View and manage all test orders</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2">
                    <Search className="w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, mobile, doctor, or agent"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-none bg-transparent focus:ring-0 focus:border-none"
                      style={{ maxWidth: 300, boxShadow: "none" }}
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2">
                    <DatePicker
                      onChange={(date) => {
                        if (!date) {
                          setSelectedDate(null);
                          return;
                        }
                        const dateStr = date.toDate().toLocaleDateString();
                        const selectedStr = selectedDate ? selectedDate.toDate().toLocaleDateString() : null;
                        // console.log("Selected and Previous: ", dateStr, selectedStr);
                        setSelectedDate(dateStr === selectedStr ? null : date);
                      }}
                      allowClear={true}
                      value={selectedDate || null}
                      className="border-none bg-transparent focus:ring-0"
                      placeholder="Filter by Date"
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg focus:ring-0 px-4 py-2">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <Select
                      value={sortBy}
                      onChange={(value) => setSortBy(value)}
                      className="border-none border-transparent bg-transparent text-gray-700"
                    >
                      <Select.Option value="schedule">Sort by Schedule</Select.Option>
                      <Select.Option value="created">Sort by Created</Select.Option>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Orders</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {testOrders?.length || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Filtered Results</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredOrders?.length || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <Search className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Current Page</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {currentPage} of {totalPages}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Spin size="large" />
                  <p className="mt-4 text-gray-600">Loading test orders...</p>
                </div>
              ) : (
                <>
                  {currentOrders?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="p-4 bg-gray-100 rounded-full mb-4">
                        <Calendar className="w-12 h-12 text-gray-400" />
                      </div>
                      <p className="text-gray-600 text-lg">No test orders found</p>
                      <p className="text-gray-500 text-sm mt-2">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="hidden md:block overflow-x-auto" style={{ maxWidth: '100vw' }}>
                        {/* Ant Design Table with sortable columns, pagination disabled (uses existing pagination) */}
                        <Table
                          dataSource={(currentOrders || []).map(item => ({ ...item, key: item._id }))}
                          pagination={false}
                          rowKey={(record) => record._id}
                          columns={(() => {
                            const getPatientName = (r) => (r.patient?.patientName || r.patientName || '').toString().toLowerCase();
                            const getDoctorName = (r) => (r.doctor?.docName || '').toString().toLowerCase();
                            const getAgentName = (r) => (r.agent?.name || '').toString().toLowerCase();
                            return [
                              {
                                title: 'Patient',
                                dataIndex: 'patientName',
                                key: 'patient',
                                sorter: (a, b) => a.testOrderID - b.testOrderID,
                                render: (_, order) => (
                                  <div className="p-2 max-w-36 overflow-x-auto">
                                    <p className="text-sm text-gray-700 flex items-center gap-2 overflow-x-auto flex text-nowrap">
                                      <span className="font-medium text-gray-900">Order ID:</span> {order.testOrderID || '<No Order ID>'}
                                    </p>
                                    <p className="font-medium text-gray-900">{order.patient?.patientName || order.patientName || '<No Name>'}</p>
                                    <p className="text-sm text-gray-600">{order.patient?.age || order.age || '---'} years, {order.patient?.gender || order.gender || 'N/A'}</p>
                                  </div>
                                ),
                              },
                              {
                                title: 'Created At',
                                dataIndex: 'createdAt',
                                key: 'createdAt',
                                sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
                                render: (text, order) => (
                                  <span className="text-sm text-green-700 font-semibold">{order.createdAt ? `${new Date(order.createdAt).toLocaleDateString()} ${new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}` : 'N/A'}</span>
                                ),
                              },
                              {
                                title: 'Last Paid',
                                dataIndex: 'lastPaymentDate',
                                key: 'lastPaid',
                                sorter: (a, b) => new Date(a.lastPaymentDate || 0) - new Date(b.lastPaymentDate || 0),
                                render: (text, order) => (
                                  <span className="text-sm text-green-700 font-semibold">{order.lastPaymentDate ? `${new Date(order.lastPaymentDate).toLocaleDateString()} ${new Date(order.lastPaymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}` : '---'}</span>
                                ),
                              },
                              {
                                title: 'Payment Type',
                                dataIndex: 'paymentType',
                                key: 'paymentType',
                                sorter: (a, b) => (a.paymentType || '').toString().toLowerCase().localeCompare((b.paymentType || '').toString().toLowerCase()),
                                render: (text) => <span className="text-sm text-blue-700">{text ? text.toString().toUpperCase() : '---'}</span>
                              },
                              {
                                title: 'Tests',
                                dataIndex: 'tests',
                                key: 'tests',
                                sorter: (a, b) => (a.tests?.length || 0) - (b.tests?.length || 0),
                                render: (tests) => (
                                  <div className="px-2 py-0 bg-green-50 max-h-40 overflow-y-auto pr-2">
                                    {tests && tests.map((test, i) => (
                                      <div key={i} className={`flex justify-between items-center gap-2 ${i < tests.length - 1 ? "mb-1 border-b border-gray-200 pb-1" : ""}`}>
                                        <TestNamePill name={test.test?.title || test.testName || '---'} />
                                        <span className="text-sm text-gray-700">৳{test.test?.price || '---'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )
                              },
                              {
                                title: 'Doctor',
                                dataIndex: ['doctor', 'docName', 'externalDoctorName'],
                                key: 'doctor',
                                sorter: (a, b) => getDoctorName(a).localeCompare(getDoctorName(b)),
                                render: (_, order) => (
                                  <div className="flex items-center gap-2 p-2">
                                    <span className="text-sm text-gray-700">{order.doctor?.docName || order?.externalDoctorName || '---'}</span>
                                  </div>
                                )
                              },
                              {
                                title: 'Agent',
                                dataIndex: ['agent', 'name'],
                                key: 'agent',
                                sorter: (a, b) => getAgentName(a).localeCompare(getAgentName(b)),
                                render: (_, order) => (
                                  <div className="flex items-center gap-2 p-2">
                                    <Building className="w-4 h-4 text-indigo-500" />
                                    <span className="text-sm text-gray-700">{order.agent?.name || '---'}</span>
                                  </div>
                                )
                              },
                              {
                                title: 'Schedule',
                                dataIndex: 'date',
                                key: 'schedule',
                                sorter: (a, b) => new Date(a.date) - new Date(b.date),
                                render: (text, order) => (
                                  <div className="p-2">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-blue-500" />
                                      <span className="text-sm text-gray-700">{new Date(order.date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-4 h-4 text-purple-500" />
                                      <span className="text-sm text-gray-700">{order.time}</span>
                                    </div>
                                  </div>
                                )
                              },
                              {
                                title: 'Total Amount',
                                dataIndex: 'totalAmount',
                                key: 'total',
                                sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
                                render: (amt) => <span className="text-sm text-gray-700">৳{amt || 0}</span>
                              },
                              {
                                title: 'Due',
                                dataIndex: 'dueAmount',
                                key: 'due',
                                sorter: (a, b) => (a.dueAmount || 0) - (b.dueAmount || 0),
                                render: (due) => <span className="text-sm text-gray-700">৳{due || 0}</span>
                              },
                              {
                                title: 'Actions',
                                key: 'actions',
                                render: (_, order) => (
                                  <div className="flex gap-1 flex-col items-center justify-center p-2">
                                    <button onClick={() => handleEditClick(order)} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"><Edit className="w-4 h-4" /> Edit</button>
                                    <button onClick={() => handlePrintReport(order)} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"><FileText className="w-4 h-4" /> Print</button>
                                    <button onClick={() => recalculateCommission(order._id)} className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-lg transition-colors text-sm font-medium">Recalculate</button>
                                    <button onClick={() => { setOrderToDelete(order); setIsConfirmDeleteOpen(true); }} className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors text-sm font-medium"><Trash2 className="w-4 h-4" /> Delete</button>
                                  </div>
                                )
                              }
                            ];
                          })()}
                        />
                      </div>

                      <div className="md:hidden">
                        {currentOrders?.map((order) => (
                          <div
                            key={order._id}
                            className="p-4 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-full">
                                  <User className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <h3 className="font-medium text-gray-900">
                                    {order.patientName}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {order.age} years, {order.gender}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {/* View button removed to reduce table width */}
                                <button
                                  onClick={() => handleEditClick(order)}
                                  className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handlePrintReport(order)}
                                  className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={() => recalculateCommission(order._id)}
                                    className="p-2 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-lg transition-colors"
                                  >
                                    Recalc
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setOrderToDelete(order);
                                    setIsConfirmDeleteOpen(true);
                                  }}
                                  className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-700">{order.mobile}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-indigo-500" />
                                <span className="text-sm text-gray-700">
                                  {order.agent?.name || "N/A"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-gray-700">
                                  {new Date(order.date).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-yellow-500" />
                                <span className="text-sm text-gray-700">
                                  Due: ৳{order.dueAmount || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="p-4 border-t border-gray-200 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                              Showing {startIndex + 1} to{" "}
                              {Math.min(endIndex, sortedOrders?.length || 0)} of{" "}
                              {sortedOrders?.length || 0} results
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                              </button>
                              <div className="flex gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                  (page) => (
                                    <button
                                      key={page}
                                      onClick={() => handlePageChange(page)}
                                      className={`px-3 py-2 rounded-lg transition-colors ${currentPage === page
                                        ? "bg-blue-600 text-white"
                                        : "bg-white border border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  )
                                )}
                              </div>
                              <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Next
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>


            <Modal
              title={
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Test Order Details
                    </h3>
                    <p className="text-sm text-gray-600">
                      Complete test order information
                    </p>
                  </div>
                </div>
              }
              open={isModalVisible}
              onCancel={handleCloseModal}
              footer={[
                <Button
                  key="delete"
                  onClick={() => {
                    setOrderToDelete(selectedOrder);
                    setIsConfirmDeleteOpen(true);
                    setIsModalVisible(false);
                  }}
                  className="bg-red-100 hover:bg-red-200 border-red-300 text-red-700"
                >
                  Delete Order
                </Button>,
                <Button
                  key="edit"
                  onClick={() => {
                    handleEditClick(selectedOrder);
                    setIsModalVisible(false);
                  }}
                  className="bg-green-100 hover:bg-green-200 border-green-300 text-green-700"
                >
                  Edit Order
                </Button>,
                <Button
                  key="back"
                  onClick={handleCloseModal}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Close
                </Button>,
              ]}
              dataSource={selectedOrder}
              width={800}
              className="custom-modal"
            >
              {selectedOrder && (
                <div className="space-y-6 pt-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">Patient Information</h4>
                      </div>
                      <button
                        type="button"
                        onClick={handlePatientEditClick}
                        className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-md font-medium transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit Patient
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-gray-900">
                            {selectedOrder.patientName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Age:</span>
                          <span className="font-medium text-gray-900">{selectedOrder.age}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gender:</span>
                          <span className="font-medium text-gray-900">
                            {selectedOrder.gender}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mobile:</span>
                          <span className="font-medium text-gray-900">
                            {selectedOrder.mobile}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium text-gray-900">
                            {selectedOrder.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Address:</span>
                          <span className="font-medium text-gray-900">
                            {selectedOrder.address || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-900">Medical Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Doctor:</span>
                        <span className="font-medium text-gray-900">
                          {selectedOrder.doctor?.docName || selectedOrder.order?.externalDoctorName || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Agent:</span>
                        <span className="font-medium text-gray-900">
                          {selectedOrder.agent?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">Tests Information</h4>
                    </div>
                    {selectedOrder.tests && selectedOrder.tests.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-purple-100">
                              <th className="text-left p-2 font-medium text-gray-700">#</th>
                              <th className="text-left p-2 font-medium text-gray-700">
                                Test Name
                              </th>
                              <th className="text-left p-2 font-medium text-gray-700">
                                Price
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrder.tests.map((test, index) => (
                              <tr key={index} className="border-t border-purple-200">
                                <td className="p-2 text-gray-900">{index + 1}</td>
                                <td className="p-2 text-gray-900">{test.testName}</td>
                                <td className="p-2 text-gray-900">৳{test.testPrice || test.test?.price}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-600">No tests information available</p>
                    )}
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-semibold text-gray-900">Financial Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Amount:</span>
                          <span className="font-medium text-gray-900">
                            ৳{selectedOrder.baseAmount || 0}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Discount Amount:</span>
                          <span className="font-medium text-gray-900">
                            ৳{selectedOrder.discountAmount || 0}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-medium text-gray-900">
                            ৳{selectedOrder.totalAmount}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Paid Amount:</span>
                          <span className="font-medium text-gray-900">
                            ৳{selectedOrder.paidAmount || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Due Amount:</span>
                          <span className="font-medium text-gray-900">
                            ৳{selectedOrder.dueAmount || 0}
                          </span>
                        </div>
                        {selectedOrder.hospitalRevenue !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Hospital Revenue:</span>
                            <span className="font-medium text-gray-900">
                              ৳{selectedOrder.hospitalRevenue.toFixed(0)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-indigo-600" />
                      <h4 className="font-semibold text-gray-900">Order Schedule</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(selectedOrder.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium text-gray-900">{selectedOrder.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium text-gray-900 ${getStatusColor(selectedOrder)}`}>
                          {(selectedOrder.dueAmount || 0) === 0 ? "Completed" : "Pending"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Modal>

            <Modal
              title={
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Edit className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit Test Order
                    </h3>
                    <p className="text-sm text-gray-600">Update test order details</p>
                  </div>
                </div>
              }
              open={isEditModalVisible}
              onCancel={handleCloseEditModal}
              footer={[
                <Button
                  key="cancel"
                  onClick={handleCloseEditModal}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  onClick={handleEdit}
                  // disabled={!dataHasChanged}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Update
                </Button>,
              ]}
              width={800}
              className="custom-modal"
            >
              {selectedOrder && (
                <div className="space-y-6 pt-4">
                  {/* ...existing code for patient, medical, and schedule info... */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">Patient Information <Edit onClick={() => {
                          toast("Ask admin to edit patient information", { id: 'edit-patient-info' })
                        }} className="w-5 h-5 text-green-600 ml-3" /> </h4>
                      </div>
                      <button
                        type="button"
                        onClick={handlePatientEditClick}
                        className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-md font-medium transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit Patient
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {/* ...existing code for patient fields... */}
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <Input disabled name="patientName" value={selectedOrder.patient?.patientName || selectedOrder.patientName || "N/A"} onChange={handleInputChange} required />
                      </div>
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                        <Input disabled type="number" name="age" value={selectedOrder.patient?.age || ""} onChange={handleInputChange} />
                      </div>
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                        <Select
                          disabled name="gender"
                          value={selectedOrder.patient?.gender || ""}
                          onChange={(value) => setFormData({ ...formData, gender: value })}
                          className="w-full"
                        >
                          <Select.Option value="">Select Gender</Select.Option>
                          <Select.Option value="Male">Male</Select.Option>
                          <Select.Option value="Female">Female</Select.Option>
                          <Select.Option value="Other">Other</Select.Option>
                        </Select>
                      </div>
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                        <Input disabled name="mobile" value={selectedOrder.patient?.mobile || ""} onChange={handleInputChange} required />
                      </div>
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input disabled type="email" name="email" value={selectedOrder.patient?.email || ""} onChange={handleInputChange} />
                      </div>
                      <div className="flex gap-4 justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <Input.TextArea disabled name="address" value={selectedOrder.patient?.address || ""} onChange={handleInputChange} rows={1} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-4 mb-3">
                      <Heart className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-900">Medical Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        onClickCapture={(e) => {
                          if (!isAdmin) {
                            toast.warn("Only admin can change");
                          }
                        }}
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex w-full justify-between">
                          Doctor Name
                          <button
                            disabled={!isAdmin}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isAdmin) return;
                              if (isExternalDoctor) {
                                setFormData({
                                  ...formData,
                                  externalDoctorName: null,
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  doctor: null
                                });
                              }
                              setIsExternalDoctor(!isExternalDoctor);
                            }}
                            className={`text-blue-800 underline hover:font-bold hover:text-blue-500 ml-2 rounded-lg px-1.5 py-0 border border-1 border-blue-800 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}> {isExternalDoctor ? "External" : "Internal"} </button>
                        </label>
                        {!isExternalDoctor ?
                          <DoctorSelect
                            disabled={!isAdmin}
                            selectedId={formData.doctor || ""}
                            onSelectID={value => {
                              if (!isAdmin) return;
                              setFormData({ ...formData, doctor: value });
                              setDataHasChanged(true);
                            }}
                            placeholder="Select Doctor"
                            className="w-full"
                          /> :
                          <Input
                            disabled={!isAdmin}
                            placeholder="Enter External Doctor Name"
                            name="doctorName"
                            value={formData.externalDoctorName}
                            onChange={(e) => {
                              if (!isAdmin) return;
                              setFormData({
                                ...formData,
                                externalDoctorName: e.target.value
                              });
                            }}
                          />
                        }
                      </div>
                      <div
                        onClickCapture={(e) => {
                          if (!isAdmin) {
                            toast.warn("Only admin can change");
                          }
                        }}
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                        <AgentSelect
                          disabled={!isAdmin}
                          selectedId={formData.agent || ""}
                          onSelectID={value => {
                            if (!isAdmin) return;
                            setFormData({ ...formData, agent: value });
                            setDataHasChanged(true);
                          }}
                          placeholder="Select Agent"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-indigo-600" />
                      <h4 className="font-semibold text-gray-900">Order Schedule</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <DatePicker value={formData.date ? moment(formData.date) : null} onChange={handleDateChange} className="w-full" format="YYYY-MM-DD" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                        <TimePicker value={formData.time ? moment(formData.time, "HH:mm") : "0:00"} onChange={handleTimeChange} className="w-full" format="HH:mm" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-1 border-black rounded-lg p-2">
                    <div className="flex justify-between">
                      <p className="text-green-800 text-sm font-medium p-2">
                        <TestTube2Icon className="inline-block w-4 h-4 mr-1" /> Tests:
                      </p>
                      <div className="flex gap-2 items-center">
                        <p className="font-semibold">Add Test</p>
                        <Button
                          type="primary"
                          shape="circle"
                          onClick={() => {
                            setIsEditModalVisible(false);
                            setIsTestSelectionModalOpen(true);
                          }}
                          className="p-3 flex items-center justify-center"
                          icon={<PlusCircleIcon />}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border border-gray-300 rounded-lg bg-white">
                      {isRemoveTestLoading &&
                        <div className="w-full flex justify-center items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        </div>
                      }

                      {!isRemoveTestLoading && (
                        selectedOrder.tests && selectedOrder.tests.length > 0 ? (
                          selectedOrder.tests.map((test, index) => (
                            <div className="space-y-1 rounded rounded-lg p-2 border border-gray-300 bg-gray-50 flex gap-2 items-center" key={index}>
                              <p key={index} className="text-gray-700">
                                {test.test?.title} - ৳{test.testPrice}
                              </p>

                              <Button
                                size="small"
                                danger
                                onClick={() => handleRemoveTest(test.test._id)}
                                className="text-xs"
                              >
                                Remove
                              </Button>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-600">No tests selected.</p>
                        )
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-semibold text-gray-900">Financial Information</h4>
                    </div>
                    <div className="relative flex gap-6 justify-between items-center mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-nowrap">Payment Type</label>

                      {/* <Select
                        className="px-1 w-[50%] rounded-xl border-2 border-indigo-300 focus:border-indigo-500 text-md font-bold"
                        placeholder="Select Payment Type"
                        bordered={false}
                        value={formData.paymentType || ""}
                        onChange={val => {
                          setFormData({ ...formData, paymentType: val });
                        }}
                      >
                        {[
                          { value: '', label: 'Select Payment Type', disabled: true },
                          { value: 'self', label: 'Self' },
                          { value: 'ctb', label: 'Commission To Broker' },
                          { value: 'nct', label: 'Non-Commissionable Transaction' },
                          { value: 'free', label: 'Free of cost' },
                          { value: 'due', label: 'Due Payment' },
                          { value: 'referred', label: 'Referred' },
                        ].map((option) => (
                          <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select> */}

                      <PaymentTypeSelect
                        value={formData.paymentType || ""}
                        onChange={value => {
                          setFormData({ ...formData, paymentType: value });
                          setDataHasChanged(true);
                        }}
                        placeholder="Select Payment Type"
                        className="w-[50%]"
                      />

                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount</label>
                        <Input type="number" name="baseAmount" value={formData.baseAmount || ""} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                        <Input type="number" name="totalAmount" value={formData.totalAmount || ""} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount</label>
                        <Input type="number" name="paidAmount" value={formData.paidAmount || ""} onChange={handleInputChange} disabled />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Amount</label>
                        <Input type="number" name="dueAmount" value={formData.dueAmount || ""} onChange={handleInputChange} disabled />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pay Due Amount</label>
                        <Input type="number" name="payDueAmount" value={formData.payDueAmount || ""} onChange={handleInputChange} min={0} max={formData.dueAmount || 0} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add Discount to Due</label>
                        <Input type="number" name="discountOnDue" value={formData.discountOnDue || ""} onChange={handleInputChange} min={0} max={formData.dueAmount || 0} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Modal>

            <Modal
              title={
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                </div>
              }
              open={isConfirmDeleteOpen}
              onCancel={() => {
                setIsConfirmDeleteOpen(false);
                setOrderToDelete(null);
              }}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => {
                    setIsConfirmDeleteOpen(false);
                    setOrderToDelete(null);
                  }}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Cancel
                </Button>,
                <Button
                  key="delete"
                  onClick={() => deleteTestOrder(orderToDelete._id)}
                  className="bg-red-100 hover:bg-red-200 border-red-300 text-red-700"
                >
                  Delete
                </Button>,
              ]}
              width={400}
              className="custom-modal"
            >
              <div className="space-y-4">
                <p className="text-gray-600 text-center">
                  Are you sure you want to delete the test order for{" "}
                  <span className="font-semibold">{orderToDelete?.patientName}</span>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm font-medium text-center">
                    ⚠️ This action cannot be undone.
                  </p>
                </div>
              </div>
            </Modal>

            {/* Edit Patient Modal */}
            <Modal
              title={
                <div className="flex items-center gap-3 border-b pb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit Patient Details
                    </h3>
                    <p className="text-sm text-gray-600">Update patient information</p>
                  </div>
                </div>
              }
              open={isPatientEditModalOpen}
              onCancel={() => setIsPatientEditModalOpen(false)}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => setIsPatientEditModalOpen(false)}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  onClick={handlePatientUpdateSubmit}
                  loading={isPatientUpdating}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Update Patient
                </Button>,
              ]}
              width={600}
              className="custom-modal"
            >
              <div className="space-y-4 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Patient Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="patientName"
                    value={patientFormData.patientName || ""}
                    onChange={handlePatientFormChange}
                    placeholder="Enter patient name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age
                    </label>
                    <Input
                      type="number"
                      name="age"
                      value={patientFormData.age || ""}
                      onChange={handlePatientFormChange}
                      placeholder="Enter age"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <Select
                      className="w-full"
                      value={patientFormData.gender || ""}
                      onChange={(val) =>
                        setPatientFormData((prev) => ({ ...prev, gender: val }))
                      }
                      options={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Other", value: "Other" },
                      ]}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="mobile"
                      value={patientFormData.mobile || ""}
                      onChange={handlePatientFormChange}
                      placeholder="Enter mobile number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={patientFormData.email || ""}
                      onChange={handlePatientFormChange}
                      placeholder="Enter email address"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <Input.TextArea
                    name="address"
                    value={patientFormData.address || ""}
                    onChange={handlePatientFormChange}
                    rows={2}
                    placeholder="Enter address"
                  />
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
};

export default TestOrdersList;